'use client';

import { useRef, useActionState, useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { useBjForm } from '@hooks/useBjForm';
import { Input, Button } from '@components/common/Forms';
import { requestPasswordResetAction } from '@auth/actions/requestPasswordResetAction';
import { requestLoginLinkAction } from '@auth/actions/requestLoginLinkAction';
import { EMAIL_OR_PHONE_PATTERN } from '@auth/constants';
import type { ForgotPassFormValues, ForgotPassFormState, ForgotPassFormAction } from '@auth/types';
import { ForgotPasswordSuccess } from '@auth/forms/ForgotPasswordSuccess';

/** Types */
interface ForgotPasswordFormProps {
  locale: Locale;
  className?: string;
}

/**
 * @description 'Forgot Password' form component.
 * - `locale`: The locale to use for translations.
 * - `className`: Optional class name to pass to the component. It will override the default class name.
 */
export const ForgotPasswordForm = (props: ForgotPasswordFormProps) => {
  // Destructure props
  const {
    locale,
    className = 'mt-5.5',
  } = props;

  // Helpers
  const { t } = useTranslation('common');

  // Used to store the form values in case of validation errors.
  // Use this to prefill the form inputs because forms are reset on submitting when using actions.
  const formValuesRef = useRef<ForgotPassFormValues | null>(null);
  const formRef = useRef<HTMLFormElement | null>(null);

  // Form hook functions
  const {
    record,
    checkFormValidation,
    handleActionErrors,
  } = useBjForm();


  // Form actions management
  // *************************
  // Bind the locale to the actions.
  const submitResetFormAction = requestPasswordResetAction.bind(null, locale);
  const submitLoginLinkAction = requestLoginLinkAction.bind(null, locale);

  // Handle form submission with validation and error handling.
  const handleSubmit = async (prevState: ForgotPassFormState, formData: FormData, action: ForgotPassFormAction) => {
    // Store the form values in the ref for validation and error handling.
    formValuesRef.current = Object.fromEntries(formData.entries());
    // Do some form validation before submitting
    if (formRef?.current) {
      const isValid = !!checkFormValidation(formRef, formData);
      if (!isValid) return prevState;
    }
    // Start the transition for the form submission if the form is valid.
    const result = await action(prevState, formData);
    // Handle API errors
    if (result.errorData) {
      handleActionErrors(result.errorData);
    }
    // All good
    return result;
  };

  // Manage the form state for the reset password action.
  const [resetPasswordState, resetPasswordAction, isPendingResetPassword] = useActionState((
    state: ForgotPassFormState,
    payload: FormData,
  ) => handleSubmit(state, payload, submitResetFormAction), {
    success: false,
  } as ForgotPassFormState);

  // Manage the form state for the login link action
  const [loginLinkState, loginLinkAction, isPendingLoginLink] = useActionState((
    state: ForgotPassFormState,
    payload: FormData,
  ) => handleSubmit(state, payload, submitLoginLinkAction), {
    success: false,
  } as ForgotPassFormState);


  // General error state for the form. Return errors not related to a specific input field.
  // ************************
  const generalError = useMemo(() => {
    const error = resetPasswordState?.errorText || loginLinkState?.errorText || null;
    return error || null;
  }, [resetPasswordState?.errorText, loginLinkState?.errorText]);


  // Render content
  // *************************
  return (
    <>
      { /* Form, no success */}
      {!resetPasswordState.success && !loginLinkState.success && (
        <>
          {/* Title */}
          <h2 className="text-2xl font-semibold tracking-tight">
            {t('register.forgotpass')}
          </h2>
          <p className="text-sm">
            {t('reset.password.empty_email_phone')}
          </p>

          { /* Reset form */}
          <form
            className={className}
            ref={formRef}
            noValidate
          >
            {/* Email input */}
            <div className="relative text-left">
              <Input
                required
                type="text"
                className="asOptional"
                placeholder={t('global.label.email_or_phone')}
                pattern={`^${EMAIL_OR_PHONE_PATTERN}$`}
                patternMismatchMessage={t('auth.email.invalid-format.message')}
                {...record('emailOrPhone', { defaultValue: formValuesRef?.current?.emailOrPhone || '' })}
              />
            </div>

            {/* Submit button */}
            <Button
              type="submit"
              color="secondary"
              rounding="full"
              className="mt-4 w-full !font-bold"
              isLoading={isPendingResetPassword}
              formAction={resetPasswordAction}
            >
              {t('account.action.reset_password')}
            </Button>

            {/* Divider */}
            <div className="my-5.5 h-2.5 border-b text-center text-sm">
              <span className="bg-surface px-4">{t('global.label.or.separator')}</span>
            </div>

            {/* Magic link */}
            <Button
              type="submit"
              color="primary"
              styling="text"
              size="sm"
              className="font-semibold"
              isLoading={isPendingLoginLink}
              formAction={loginLinkAction}
            >
              {t('account.action.login_link')}
            </Button>

            {/* Display a general authentication error if any */}
            {generalError && (
              <div className="mt-5.5 rounded bg-error/10 p-2 text-sm text-error">
                {generalError}
              </div>
            )}
          </form>
        </>
      )}

      {/* Reset the password success message */}
      {resetPasswordState.success && (
        <ForgotPasswordSuccess
          type="reset"
          emailOrPhone={formValuesRef.current?.emailOrPhone}
        />
      )}

      {/* Login link success message */}
      {loginLinkState.success && (
        <ForgotPasswordSuccess
          type="loginLink"
          emailOrPhone={formValuesRef.current?.emailOrPhone}
        />
      )}
    </>
  );
};
